import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';

const PlaybookTOC = () => {
  const { playbookId, sectionId } = useParams();
  const navigate = useNavigate();
  const [headings, setHeadings] = useState([]);
  const [activeHeading, setActiveHeading] = useState('');
  
  // Scan the page for headings
  useEffect(() => {
    const getHeadings = () => {
      // Find all h1, h2, h3 elements in the main content area
      const headingElements = Array.from(document.querySelectorAll('main h1, main h2, main h3'));
      
      const headingData = headingElements.map((heading, index) => {
        // Create an id if the heading doesn't have one
        if (!heading.id) {
          const id = `heading-${index}`;
          heading.id = id;
        }
        
        return {
          id: heading.id,
          text: heading.textContent,
          level: Number(heading.tagName.substring(1)) // Extract number from h1, h2, h3
        };
      });
      
      setHeadings(headingData);
    };
    
    // Allow time for the DOM to update
    const timer = setTimeout(() => {
      getHeadings();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [sectionId]); // Re-run when section changes
  
  // Set up intersection observer for headings
  useEffect(() => {
    if (!headings.length) return;
    
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -60% 0px', // Adjusted for better detection
      threshold: 0
    };
    
    const handleIntersect = (entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveHeading(entry.target.id);
        }
      });
    };
    
    const observer = new IntersectionObserver(handleIntersect, observerOptions);
    
    // Observe all heading elements
    headings.forEach(heading => {
      const element = document.getElementById(heading.id);
      if (element) {
        observer.observe(element);
      }
    });
    
    return () => observer.disconnect();
  }, [headings]);

  const handleHeadingClick = (e, id) => {
    e.preventDefault();
    
    const element = document.getElementById(id);
    if (element) {
      // Add a temporary padding to the element to account for the header
      const originalPadding = element.style.paddingTop;
      const originalScrollMargin = element.style.scrollMarginTop;
      
      element.style.scrollMarginTop = '90px';
      
      element.scrollIntoView({ 
        behavior: 'auto',
        block: 'start'
      });
      
      // Reset the styles after scrolling
      setTimeout(() => {
        element.style.scrollMarginTop = originalScrollMargin;
      }, 1000);
      
      setActiveHeading(id);
    }
  };

  // If no headings found, don't render anything
  if (headings.length === 0) {
    return null;
  }

  return (
    <div className="fixed right-6 top-24 w-48 z-10 hidden lg:block h-[calc(100vh-7rem)] overflow-y-auto">
      <div className="sticky top-0 pt-2 pb-8">
        <h4 className="text-sm font-medium text-white mb-4 px-4">On this page</h4>
        
        <nav className="flex flex-col">
          {headings.map((heading) => {
            const isActive = activeHeading === heading.id;
            const isH1 = heading.level === 1;
            const isH2 = heading.level === 2;
            const isH3 = heading.level === 3;
            
            // Linear-like styling based on heading level
            let paddingClass = "pl-4";
            if (isH2) paddingClass = "pl-6";
            if (isH3) paddingClass = "pl-8";
            
            return (
              <a
                key={heading.id}
                href={`#${heading.id}`}
                className={`
                  py-2 text-[13px] leading-tight transition-colors relative
                  ${paddingClass}
                  ${isActive 
                    ? 'text-white font-medium' 
                    : 'text-gray-400 hover:text-gray-200'}
                  ${isH1 ? 'font-medium' : ''}
                `}
                onClick={(e) => handleHeadingClick(e, heading.id)}
      >
                {/* Simple left border for active state - Linear style */}
                {isActive && (
                  <div 
                    className="absolute left-0 top-0 bottom-0 w-[2px] bg-white/60"
                    aria-hidden="true"
                  />
                )}
                
                <span className="line-clamp-2">
                  {heading.text}
          </span>
              </a>
            );
          })}
        </nav>
        </div>
    </div>
  );
};

export default PlaybookTOC; 